import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from "@sentry/react";
import { ClerkProvider } from '@clerk/clerk-react';
import App from './App';
import analytics from './services/AnalyticsService';
import { resolveSentryEnvironment, resolveSentryRelease, resolveSentryTags } from './utils/sentryConfig';
import './styles/index.css';

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY || import.meta.env.CLERK_PUBLISHABLE_KEY;
// import 'bootstrap/dist/css/bootstrap.min.css'; -- Removed to prevent style conflicts with V2 "Stone" theme

// Helper to safely parse sample rates with validation
const getSampleRate = (value, defaultValue = 1.0) => {
  const rate = parseFloat(value || defaultValue);
  if (isNaN(rate) || rate < 0 || rate > 1) {
    if (import.meta.env.DEV) {
      console.warn(`Invalid sample rate: ${value}, using ${defaultValue}`);
    }
    return defaultValue;
  }
  return rate;
};

// Initialize Sentry for frontend error monitoring
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const sentryEnvironment = resolveSentryEnvironment(import.meta.env);
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,

    // Performance Monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Disable session replay by default (can be enabled per-session)
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance monitoring sample rate (0.0 to 1.0)
    tracesSampleRate: getSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE, 1.0),

    // Session replay sample rate (0.0 to 1.0)
    // Only capture replays on error by default
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,

    // production / development / preview (Railway PR builds) — see utils/sentryConfig.js
    environment: sentryEnvironment,

    // Release tracking (the Dockerfile falls back to Railway's commit SHA)
    release: resolveSentryRelease(import.meta.env),

    initialScope: { tags: resolveSentryTags(import.meta.env) },

    // Don't send PII by default
    sendDefaultPii: false,

    // Filter out non-error events
    beforeSend(event, hint) {
      // Don't send events for cancelled requests
      const error = hint.originalException;
      if (error && error.message && error.message.includes('cancelled')) {
        return null;
      }
      return event;
    },

    // Ignore specific errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'chrome-extension://',
      'moz-extension://',
      // Random network errors
      'NetworkError',
      'Network request failed',
      'Failed to fetch',
      // React errors that are already logged
      'ResizeObserver loop limit exceeded',
    ],
  });

  if (import.meta.env.DEV) {
    console.log(`Sentry initialized for environment: ${sentryEnvironment}`);
  }
} else if (import.meta.env.DEV) {
  console.log('Sentry DSN not configured - error monitoring disabled');
}

// Initialize analytics. Defaults to no-op when VITE_ANALYTICS_PROVIDER is unset,
// so the app is safe to deploy without configuring a provider.
analytics.init({
  provider: import.meta.env.VITE_ANALYTICS_PROVIDER || 'noop',
  key: import.meta.env.VITE_POSTHOG_KEY,
  host: import.meta.env.VITE_POSTHOG_HOST,
});

const root = ReactDOM.createRoot(document.getElementById('root'));

// ClerkProvider sits OUTSIDE React.StrictMode intentionally. StrictMode
// double-mounts components in development to surface side-effect bugs, but
// ClerkProvider owns long-lived auth state (token refresh, session management)
// and its <SignUp> child re-runs prepareEmailAddressVerification on remount —
// causing duplicate verification-code emails during registration. Exempting
// only the auth provider keeps StrictMode's diagnostics for the rest of the app.
root.render(
  CLERK_PUBLISHABLE_KEY ? (
    <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY} afterSignOutUrl="/clerk-login">
      <React.StrictMode>
        <App />
      </React.StrictMode>
    </ClerkProvider>
  ) : (
    <React.StrictMode>
      <App />
    </React.StrictMode>
  )
);
